import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import clsx from 'clsx';
import { memo, useCallback, useRef } from 'react';

import type { SideNavLink } from '../types';

import { CountBadge } from '../../../../CountBadge/CountBadge';
import { Icon } from '../../../../Icon/Icon';
import { useSortableItemContext } from '../../../../SortableList/context';
import { SortableList } from '../../../../SortableList/SortableList';
import { useSideNavContext } from '../context';
import { usePinnedSectionPresence, type PinnedRow } from '../pinnedRows';
import { isLinkCurrent } from '../utils';
import { SideNavControl } from './SideNavControl';
import { SideNavPinButton } from './SideNavPinButton';

const DRAG_MODIFIERS = [restrictToVerticalAxis, restrictToParentElement];
const SORTABLE_LIST_PRIVATE = {
  isSortable: (row: PinnedRow) => !row.isLeaving,
};

interface SideNavPinnedRowProps {
  row: PinnedRow;
  /** Present only when the host can reorder. */
  isReorderable: boolean;
}

function SideNavPinnedRowComponent({
  isReorderable,
  row,
}: SideNavPinnedRowProps) {
  const { activeKey, labels, onActivate, pinControl, regionAttributes } =
    useSideNavContext();
  const sortable = useSortableItemContext();
  const { isEntering, isLeaving, link } = row;
  const isCurrent = isLinkCurrent(link.key, activeKey);

  return (
    <div
      aria-hidden={isLeaving}
      className={clsx(
        'eds-side-nav--pinned-row',
        isCurrent && 'eds-side-nav--pinned-row--active',
        sortable?.isDragging && 'eds-side-nav--pinned-row--dragging',
        isEntering && 'eds-side-nav--pinned-row--entering',
        isLeaving && 'eds-side-nav--pinned-row--leaving',
      )}
    >
      {isReorderable && (
        <button
          ref={sortable?.setActivatorNodeRef}
          type="button"
          aria-label={labels.reorder(link.label)}
          className="eds-side-nav--drag-handle"
          {...sortable?.attributes}
          {...sortable?.listeners}
        >
          <Icon icon="drag-and-drop-indicator" size="s" />
        </button>
      )}
      <SideNavControl
        className="eds-side-nav--pinned-control"
        isCurrent={isCurrent}
        link={link}
        onActivate={onActivate}
      >
        <span className="eds-side-nav--pinned-label">{link.label}</span>
      </SideNavControl>
      {pinControl && (
        <SideNavPinButton
          dataAttributes={regionAttributes.unpin}
          isPinned
          label={labels.unpin(link.label)}
          onToggle={() => pinControl.onToggle(link.key)}
        />
      )}
    </div>
  );
}

const SideNavPinnedRow = memo(SideNavPinnedRowComponent);

interface SideNavPinnedSectionProps {
  links: SideNavLink[];
  onReorder?: (keys: string[]) => void;
}

/**
 * The pinned entries, above every other section. Only shows once something is
 * pinned, since an empty heading would be permanent chrome for the people who
 * never pin anything, and opens and closes on the transitions rather than
 * appearing and vanishing under the rest of the nav.
 */
export const SideNavPinnedSection = memo(function SideNavPinnedSection({
  links,
  onReorder,
}: SideNavPinnedSectionProps) {
  const { labels, regionAttributes } = useSideNavContext();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { isLeaving, rows } = usePinnedSectionPresence(links, sectionRef);

  const getId = useCallback((row: PinnedRow) => row.link.key, []);
  const handleReorder = useCallback(
    (nextRows: PinnedRow[]) =>
      onReorder?.(
        nextRows.filter((row) => !row.isLeaving).map((row) => row.link.key),
      ),
    [onReorder],
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <div
      aria-hidden={isLeaving}
      className={clsx(
        'eds-side-nav--pinned eds-side-nav--reveal',
        isLeaving && 'eds-side-nav--reveal--leaving',
      )}
      ref={sectionRef}
      {...regionAttributes.pinned}
    >
      <div className="eds-side-nav--reveal-content">
        <div className="eds-side-nav--pinned-header">
          <span className="eds-side-nav--overline">{labels.pinned}</span>
          <span
            aria-label={labels.pinnedCount(links.length)}
            className="eds-side-nav--pinned-count"
          >
            <CountBadge
              count={links.length}
              icon={{ filled: true, key: 'pin' }}
              size="s"
            />
          </span>
        </div>
        <SortableList
          getId={getId}
          items={rows}
          modifiers={DRAG_MODIFIERS}
          onReorder={handleReorder}
          __private={SORTABLE_LIST_PRIVATE}
        >
          {(row) => (
            <SideNavPinnedRow isReorderable={Boolean(onReorder)} row={row} />
          )}
        </SortableList>
        <div aria-hidden="true" className="eds-side-nav--divider" />
      </div>
    </div>
  );
});
